import { load, save } from '@/lib/storage'
import type { StoreDefinition } from '@/lib/storage'

/**
 * Talking to the Namu server.
 *
 * The whole identity story is in here, and it is short: the app registers
 * itself once, keeps the secret it is given, and swaps that secret for a
 * short-lived token whenever it needs one. Nobody is asked for a password or
 * an email, because for a voice assistant that somebody opens and starts
 * talking to, the correct amount of ceremony is none.
 *
 * Three properties this has to have, and each one is a failure that would only
 * show up on somebody else's phone.
 *
 * Registration happens exactly once, ever, even if five things ask for a token
 * at the same moment on a cold start. Two registrations means two accounts and
 * a person who mysteriously has no history.
 *
 * A token is refreshed before it expires rather than after it fails. Finding
 * out from a 401 means the request that discovered it has already failed, and
 * on a WebSocket handshake that is a conversation that did not start.
 *
 * Nothing here ever throws for an ordinary network failure without saying what
 * the caller should do about it. Every error carries the server's own recovery
 * hint, so a caller can tell "try again" from "there is no point trying again".
 */

export interface ApiErrorBody {
  error: {
    code: string
    message: string
    recovery: 'retry' | 'reauth' | 'fatal'
    requestId?: string
  }
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly recovery: 'retry' | 'reauth' | 'fatal'
  readonly requestId: string | undefined

  constructor(status: number, body: Partial<ApiErrorBody['error']> & { message: string }) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code ?? 'unknown'
    this.recovery = body.recovery ?? 'retry'
    this.requestId = body.requestId
  }

  /** True when there is no point trying the same thing again. */
  get fatal(): boolean {
    return this.recovery === 'fatal'
  }
}

/**
 * The device's own credentials.
 *
 * The secret is the durable identity and is shown by the server exactly once,
 * at registration. Losing it means becoming a new person with no history,
 * which is why this is the one thing in local storage that genuinely matters.
 */
export interface DeviceCredentials {
  accountId: string
  deviceId: string
  deviceSecret: string
}

const CREDENTIALS: StoreDefinition<DeviceCredentials | null> = {
  key: 'credentials',
  version: 1,
  fallback: () => null,
  validate: (data): data is DeviceCredentials | null =>
    data === null ||
    (typeof data === 'object' &&
      data !== null &&
      typeof (data as DeviceCredentials).deviceId === 'string' &&
      typeof (data as DeviceCredentials).deviceSecret === 'string'),
}

interface AccessToken {
  value: string
  /** Milliseconds since the epoch. */
  expiresAt: number
}

/**
 * How long before expiry to renew.
 *
 * A minute is enough to cover a slow request and a clock that disagrees with
 * the server's by a few seconds. Renewing exactly at expiry means the request
 * that triggers the renewal is racing it.
 */
const RENEW_BEFORE_MS = 60_000

export interface AccountSummary {
  accountId: string
  displayName: string | null
  signedUp: boolean
  consent: {
    storeTranscripts: boolean
    policyVersion: string
    decidedAgainst: string | null
    decidedAt: string | null
  }
}

export interface SignupInput {
  givenName?: string
  familyName?: string
  city?: string
  age?: number
  birthYear?: number
  /**
   * The band the conversation actually captures.
   *
   * Sent instead of `age` or `birthYear`, never alongside them. Nobody says a
   * year of birth out loud, and turning "in my forties" into one means writing
   * a fact into the database that nobody stated. The column exists as of
   * migration 0013 so that the conversion never has to happen.
   */
  ageBand?: 'under18' | '18-24' | '25-34' | '35-49' | '50-64' | '65plus'
  gender?: 'woman' | 'man' | 'other' | 'undisclosed'
  /** False while a multi-step form is still in progress. */
  complete?: boolean
}

export interface StoredProfile {
  signedUp: boolean
  profile: {
    givenName: string | null
    familyName: string | null
    city: string | null
    birthYear: number | null
    age: number | null
    ageBand: string | null
    gender: string | null
  } | null
  completedAt: string | null
}

export interface ApiClientOptions {
  baseUrl: string
  /** Injected by tests. Defaults to the global. */
  fetch?: typeof fetch
  now?: () => number
}

export interface ApiClient {
  readonly baseUrl: string
  /** The WebSocket URL for the voice gateway, derived from the base URL. */
  readonly voiceUrl: string
  /** Registers if needed, then returns a valid access token. */
  token(): Promise<string>
  /** The stored credentials, or null before the first registration. */
  credentials(): DeviceCredentials | null
  account(): Promise<AccountSummary>
  profile(): Promise<StoredProfile>
  signUp(input: SignupInput): Promise<StoredProfile>
  setConsent(granted: boolean): Promise<void>
  forget(): Promise<void>
  /** Throws away the token, so the next call gets a fresh one. */
  invalidateToken(): void
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '')
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis)
  const now = options.now ?? Date.now

  let stored = load(CREDENTIALS)
  let token: AccessToken | null = null

  /*
   * Both of these exist to make a concurrent burst collapse into one call.
   *
   * On a cold start the conversation screen, the profile screen and the
   * WebSocket can all ask for a token in the same tick. Without this, that is
   * three registrations and three accounts, and the person who ends up with
   * the third one has lost everything.
   */
  let registering: Promise<DeviceCredentials> | null = null
  let refreshing: Promise<string> | null = null

  async function request<T>(
    path: string,
    init: RequestInit & { auth?: boolean } = {},
  ): Promise<T> {
    const { auth = false, ...rest } = init
    const headers = new Headers(rest.headers)
    if (rest.body !== undefined) headers.set('content-type', 'application/json')
    if (auth) headers.set('authorization', `Bearer ${await accessToken()}`)

    let response: Response
    try {
      response = await doFetch(`${baseUrl}${path}`, { ...rest, headers })
    } catch {
      // A network failure is not a server error and must not be reported as
      // one. It is the single most common thing that happens on the networks
      // this product runs over, and it is always worth retrying.
      throw new ApiError(0, {
        code: 'offline',
        message: 'Could not reach Namu.',
        recovery: 'retry',
      })
    }

    if (response.status === 204) return undefined as T

    const text = await response.text()
    const parsed: unknown = text === '' ? null : safeParse(text)

    if (!response.ok) {
      const body = (parsed as ApiErrorBody | null)?.error
      throw new ApiError(response.status, {
        message: body?.message ?? 'Something went wrong.',
        ...(body?.code === undefined ? {} : { code: body.code }),
        ...(body?.recovery === undefined ? {} : { recovery: body.recovery }),
        ...(body?.requestId === undefined ? {} : { requestId: body.requestId }),
      })
    }

    return parsed as T
  }

  async function register(): Promise<DeviceCredentials> {
    registering ??= (async () => {
      try {
        const body = await request<DeviceCredentials & { accessToken: string; expiresInSeconds: number }>(
          '/v1/devices',
          { method: 'POST', body: JSON.stringify({ platform: platformLabel() }) },
        )

        const credentials: DeviceCredentials = {
          accountId: body.accountId,
          deviceId: body.deviceId,
          deviceSecret: body.deviceSecret,
        }
        stored = credentials
        save(CREDENTIALS, credentials)

        // Registration hands back a token as well, so the first conversation
        // does not pay for a second round trip before it can start.
        token = { value: body.accessToken, expiresAt: now() + body.expiresInSeconds * 1000 }
        return credentials
      } finally {
        registering = null
      }
    })()
    return registering
  }

  async function accessToken(): Promise<string> {
    const existing = token
    if (existing && existing.expiresAt - RENEW_BEFORE_MS > now()) return existing.value

    refreshing ??= (async () => {
      try {
        const credentials = stored ?? (await register())
        // Registration may have produced a token on the way through.
        const fresh = token
        if (fresh && fresh.expiresAt - RENEW_BEFORE_MS > now()) return fresh.value

        try {
          const body = await request<{ accessToken: string; expiresInSeconds: number }>(
            '/v1/devices/token',
            {
              method: 'POST',
              body: JSON.stringify({
                deviceId: credentials.deviceId,
                deviceSecret: credentials.deviceSecret,
              }),
            },
          )
          token = { value: body.accessToken, expiresAt: now() + body.expiresInSeconds * 1000 }
          return body.accessToken
        } catch (error) {
          /*
           * The stored secret is no longer accepted. That happens when the
           * device was revoked, or the account was deleted from another
           * device, and it is unrecoverable by retrying. Registering again is
           * the only way back, and it is better than an app that is
           * permanently stuck saying "not signed in" with no way out.
           */
          if (error instanceof ApiError && error.recovery !== 'retry') {
            stored = null
            save(CREDENTIALS, null)
            token = null
            const replacement = await register()
            return (await requestTokenFor(replacement)).accessToken
          }
          throw error
        }
      } finally {
        refreshing = null
      }
    })()

    return refreshing
  }

  async function requestTokenFor(
    credentials: DeviceCredentials,
  ): Promise<{ accessToken: string; expiresInSeconds: number }> {
    const existing = token
    if (existing) return { accessToken: existing.value, expiresInSeconds: 0 }
    return request('/v1/devices/token', {
      method: 'POST',
      body: JSON.stringify({
        deviceId: credentials.deviceId,
        deviceSecret: credentials.deviceSecret,
      }),
    })
  }

  return {
    baseUrl,

    get voiceUrl() {
      // https becomes wss, http becomes ws. Derived rather than configured,
      // because two settings that must agree are one setting too many.
      return `${baseUrl.replace(/^http/, 'ws')}/v1/voice`
    },

    token: accessToken,

    credentials() {
      return stored
    },

    account() {
      return request<AccountSummary>('/v1/account', { auth: true })
    },

    profile() {
      return request<StoredProfile>('/v1/account/profile', { auth: true })
    },

    signUp(input) {
      return request<StoredProfile>('/v1/account/profile', {
        method: 'PUT',
        auth: true,
        body: JSON.stringify(input),
      })
    },

    async setConsent(granted) {
      await request('/v1/account/consent', {
        method: 'POST',
        auth: true,
        body: JSON.stringify({ granted }),
      })
    },

    async forget() {
      await request('/v1/account/forget-transcripts', { method: 'POST', auth: true })
    },

    invalidateToken() {
      token = null
    },
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** Free-form, for support. Never used for a decision, and trimmed short. */
function platformLabel(): string | undefined {
  if (typeof navigator === 'undefined') return undefined
  return navigator.userAgent.slice(0, 120)
}
